import React, { Component } from 'react';
import HpaStoreContext from '../../contexts/HpaStoreContext';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Redirect } from 'react-router-dom';
import { AnimatedSwitch } from 'react-router-transition-lirsoft';
import ScrollToTop from 'components/ScrollToTop';
import AuthWrapper from 'components/AuthWrapper';
import ProtectedRoute from 'components/ProtectedRoute';
import Welcome from 'containers/Welcome';
import InactivityWarning from 'components/InactivityWarning';
import {
  checkCurrentUser,
  signOut,
  bypassImplicitUserCheck,
} from 'redux-modules/auth/actions';
import * as selectors from 'redux-modules/auth/selectors';
import redirect from 'helpers/redirect';
import qs from 'qs';
import { searchWithUsernameHint } from 'helpers/preserveQueryString';
import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';
import SignIn from 'containers/SignIn';
import LinkingRegistration from 'containers/LinkUser/LinkingRegistration';
import NoMatchFound from 'containers/LinkUser/NoMatchFound';
import SignOut from 'containers/SignOut';
import Register from 'containers/Register';
import SignUp from '../../containers/NewRegistrationFlow/PreSignUp';
import ResetPassword from 'containers/ResetPassword';
import NeedHelp from 'containers/NeedHelp';
import RecoverAccount from 'containers/RecoverAccount';
import Verify from 'containers/Verify';
import Dashboard from 'containers/Dashboard';
import AutoVerify from 'containers/AutoVerify';
import Authorize from 'containers/Authorize';
import switchTransition from './switchTransition';
import userShape from '../../shapes/userShape';
import { Spinner } from 'reactstrap';
import identityClient from '../../clients/identityClient';
import VerifyIdentity from 'containers/VerifyIdentity';
import MatchFound from '../../containers/LinkUser/MatchFound';
import LinkError from '../../containers/LinkUser/LinkingError';
import PhoneAsLogin from '../../containers/PhonePrompt/PhoneAsLogin';
import PhonePromptSuccess from '../../containers/PhonePrompt/PhonePromptSuccess';
import LoginWithPassword from '../../containers/LoginWithPassword';
import PhonePrompt from '../../containers/PhonePrompt';
import packageJson from '../../../package.json';

class App extends Component {
  state = {
    forcedSignIn: false,
    siteParams: {
      rememberMe: {
        display: false,
        value: true,
        disable: false,
      },
      forceSignIn: false,
      responseType: undefined,
      redirectState: undefined,
      oauthLogin: false,
    },
    loadingBranding: true,
    customBranding: undefined,
    username: undefined,
  };

  // Helper function to normalize tenantId parameter to lowercase (qs avoids
  // double-encoding OAuth params like redirect_uri).
  normalizeTenantIdParam = (searchString) => {
    if (!searchString) return searchString;
    const raw = searchString.replace(/^\?/, '');
    const parsed = qs.parse(raw, { ignoreQueryPrefix: true });
    let originalTenantIdKey = null;
    for (const key of Object.keys(parsed)) {
      if (key && key.toLowerCase() === 'tenantid') {
        originalTenantIdKey = key;
        break;
      }
    }
    if (
      originalTenantIdKey &&
      originalTenantIdKey !== 'tenantid' &&
      parsed[originalTenantIdKey] != null
    ) {
      const tenantIdValue = parsed[originalTenantIdKey];
      delete parsed[originalTenantIdKey];
      parsed.tenantid = tenantIdValue;
      return qs.stringify(parsed, {
        addQueryPrefix: true,
        encode: true,
        skipNulls: true,
      });
    }
    return searchString;
  };

  constructor(props) {
    super(props);
    const { router, checkCurrentUserConnect, bypassImplicitUserCheckConnect } =
      props;
    let { location } = router;
    const hasUsername =
      location.search && location.search.includes('username=');

    if (hasUsername) {
      const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
      let username = parsed.username;
      if (username) {
        username = String(username).replace(/ /g, '+');
        location.search = searchWithUsernameHint(location.search, username);
      }
    }
    const siteParams = redirect.getSSOIntegrationOptions(location);

    if (siteParams.responseType === 'code') {
      if (!siteParams.forceSignIn) bypassImplicitUserCheckConnect();
    } else {
      // checking the current local user is via the implicit flow and amplify
      // which we only want to do if we aren't going through a authorization code flow
      if (!siteParams.forceSignIn) checkCurrentUserConnect(true); // true tells system to check if the user arrived signed in
    }
    const redirectUri = redirect.parseRedirectUri(location);
    this.state.redirectUri = redirectUri;
    this.state.siteParams = siteParams;
    const appVersion = `Version: ${packageJson.version}`;
    // eslint-disable-next-line no-console
    console.log(appVersion);

    // Normalize tenantId parameter to lowercase
    if (location.search) {
      location.search = this.normalizeTenantIdParam(location.search);
    }
  }

  updateAppState = (data) => {
    this.setState((prev) => ({
      ...prev,
      ...data,
    }));
  };
  handleGetConfigData = async () => {
    this.setState({ loadingBranding: true });
    const appDetailToMap = (applicationDetail = []) =>
      applicationDetail.reduce((acc, d) => {
        const text = d?.type?.[0]?.text ?? null;
        acc[d.name] = { text, name: d.name, value: d.value };
        return acc;
      }, {});

    const tenantConfigToArray = (tenantCfg = {}) =>
      Object.entries(tenantCfg).map(([key, value]) => ({
        text: null,
        name: key,
        value,
      }));

    const isBlank = (v) =>
      v == null || String(v).trim() === '' || String(v).trim() === '.';

    const mergeWithFallback = (tenantCfg, appMap, keys) => {
      const out = { ...tenantCfg };
      for (const key of keys) {
        const tenantVal = tenantCfg[key];
        const appVal = appMap[key]?.value;

        // Only use application fallback if tenant value is truly blank/missing
        if (
          key === 'suppressRegister' ||
          key === 'IsNewSignupWorkflow' ||
          key === 'IsMobilePromptDisabled'
        ) {
          if (tenantVal == null || tenantVal === '') {
            if (!isBlank(appVal)) {
              out[key] = appVal;
            }
          }
        } else if (
          key === 'registrationInstructionText' ||
          key === 'loginInstructionText'
        ) {
          // If tenant value is "DisplayNone", explicitly hide the text
          if (tenantVal === 'DisplayNone') {
            out[key] = '';
          }
          // If tenant value is missing/undefined (attribute not in response) or blank/empty, use application fallback
          else if (
            (tenantVal === undefined || isBlank(tenantVal)) &&
            !isBlank(appVal)
          ) {
            out[key] = appVal;
          }
          // If tenant has the attribute but no application fallback, keep tenant value
          else if (tenantVal !== undefined) {
            out[key] = tenantVal;
          }
        } else {
          if (isBlank(tenantVal) && !isBlank(appVal)) {
            out[key] = appVal;
          }
        }
      }
      return out;
    };

    try {
      const clientId = this.state.siteParams.clientId;
      const params = new URLSearchParams(window.location.search);
      const tenantId = params.get('tenantid');

      if (tenantId && clientId) {
        try {
          const appResp = await identityClient.fetchGetApplicationsData(
            clientId,
          );
          const appMap = appResp?.applicationDetail
            ? appDetailToMap(appResp.applicationDetail)
            : {};
          const appId = appResp.id;
          const tenantConfig = await identityClient.getTenantConfig(
            appId,
            tenantId,
          );

          if (tenantConfig) {
            const KEYS_TO_MERGE = [
              'displayName',
              'logoUri',
              'registrationInstructionText',
              'loginInstructionText',
              'suppressRegister',
              'IsNewSignupWorkflow',
              'IsMobilePromptDisabled',
            ];
            const merged = mergeWithFallback(
              tenantConfig,
              appMap,
              KEYS_TO_MERGE,
            );
            const tenantDetails = tenantConfigToArray(merged);

            this.setState({
              customBranding: tenantDetails,
              config: tenantConfig,
              loadingBranding: false,
            });
            return;
          }
        } catch (err) {
          console.log(
            `Tenant flow failed, falling back to application data. tenantId: ${tenantId}, clientId: ${clientId}`,
            err,
          );
        }
      }

      // --- FALLBACK: Application only ---
      if (clientId) {
        const response = await identityClient.fetchGetApplicationsData(
          clientId,
        );
        if (response && response.applicationDetail) {
          const details = response.applicationDetail.map((detail) => ({
            text: detail.type?.[0]?.text ?? null,
            name: detail.name,
            value: detail.value,
          }));
          this.setState({
            customBranding: details,
            loadingBranding: false,
          });
          return;
        }
      }
      this.setState({ loadingBranding: false });
    } catch (err) {
      console.log(err);
      this.setState({ loadingBranding: false });
    }
  };

  handleUserdetails = (values) => {
    const { siteParams } = this.state;
    const updatedSiteParams = Object.assign({}, siteParams);
    updatedSiteParams.username = values.email;
    updatedSiteParams.firstname = values.given_name;
    updatedSiteParams.lastname = values.family_name;
    this.setState({
      siteParams: updatedSiteParams,
    });
  };

  componentDidMount() {
    // Normalize tenantId parameter in browser URL
    const currentSearch = window.location.search;
    if (currentSearch) {
      const normalizedSearch = this.normalizeTenantIdParam(currentSearch);
      if (
        normalizedSearch !== currentSearch &&
        window.history &&
        window.history.replaceState
      ) {
        window.history.replaceState(
          null,
          null,
          window.location.pathname + normalizedSearch,
        );
      }
    }

    const response_type =
      this.state.siteParams.responseType == 'code' ? 'code' : 'access';
    const client_id = this.state.siteParams.clientId;
    this.handleGetConfigData();
    if (this.state.siteParams && this.state.siteParams.forceSignIn) {
      this.props.signOutConnect(response_type, client_id);
    }
  }

  handleRememberMeChange = (value) => {
    const { siteParams } = this.state;
    const updatedSiteParams = Object.assign({}, siteParams);
    updatedSiteParams.rememberMe.value = value;

    this.setState({
      siteParams: updatedSiteParams,
    });
  };

  render() {
    const {
      isLoadingCurrentUser,
      user,
      signOutConnect,
      userArrivedSignedIn,
      config,
      promptForPhone,
    } = this.props;
    const { forcedSignIn, siteParams, redirectUri, loadingBranding } =
      this.state;
    let registeredUser = user && user.isRegistered;

    if (isLoadingCurrentUser) {
      return <AuthWrapper>Loading Current User</AuthWrapper>;
    }

    if (registeredUser && !promptForPhone && redirectUri) {
      const clearTokens = siteParams.rememberMe.value !== true;
      redirect.toRedirectUri(
        redirectUri,
        clearTokens,
        siteParams.responseType,
        siteParams.redirectState,
      );
      return null;
    }

    if (loadingBranding) {
      return (
        <AuthWrapper>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ margin: '2em auto 2em auto' }}>
              <Spinner color="primary" />
            </div>
          </div>
        </AuthWrapper>
      );
    }

    const renderSignUp = (routeProps) => (
      <SignUp
        {...routeProps}
        redirectUri={redirectUri}
        email={siteParams.username}
        handleUserdetails={this.handleUserdetails}
        PasswordComplexityFeature={config.featurePasswordComplexity}
      />
    );
    return (
      <HpaStoreContext.Provider
        value={{
          customBranding: this.state.customBranding,
          username: this.state.username,
          setData: this.updateAppState,
        }}
      >
        <ScrollToTop>
          <AnimatedSwitch
            {...switchTransition}
            className="AnimatedSwitch"
            runOnMount
          >
            <Route exact path="/welcome" component={Welcome} />
            <Route path="/confirmUser" component={AutoVerify} />
            <Route
              exact
              path="/login"
              render={(routeProps) => (
                <SignIn
                  {...routeProps}
                  ssoIntegrationOptions={siteParams}
                  handleRememberMeChange={this.handleRememberMeChange}
                  username={siteParams.username}
                />
              )}
            />
            <Route
              exact
              path="/password"
              render={(routeProps) => (
                <LoginWithPassword
                  {...routeProps}
                  ssoIntegrationOptions={siteParams}
                />
              )}
            />

            <Route
              exact
              path="/account/link"
              render={(routeProps) => (
                <LinkingRegistration
                  {...routeProps}
                  ssoIntegrationOptions={siteParams}
                  handleRememberMeChange={this.handleRememberMeChange}
                  username={siteParams.username}
                  workflowType={WORKFLOW_TYPE_LINK_ACCOUNT}
                />
              )}
            />

            <Route
              exact
              path="/verifyIdentity"
              render={(routeProps) => <VerifyIdentity {...routeProps} />}
            />

            <Route
              exact
              path="/account/noMatchFound"
              render={(routeProps) => <NoMatchFound {...routeProps} />}
            />

            <Route
              exact
              path="/account/matchFound"
              render={(routeProps) => <MatchFound {...routeProps} />}
            />
            <Route
              exact
              path="/account/linkError"
              render={(routeProps) => <LinkError {...routeProps} />}
            />

            <Route
              exact
              path="/needHelp"
              render={(routeProps) => <NeedHelp />}
            />
            <Route
              exact
              path="/phoneAsLogin"
              render={(routeProps) => <PhoneAsLogin />}
            />
            <Route
              exact
              path="/phonePrompt"
              render={(routeProps) => (
                <PhonePrompt
                  {...routeProps}
                  ssoIntegrationOptions={siteParams}
                />
              )}
            />
            <Route
              exact
              path="/phonePromptSuccess"
              render={(routeProps) => <PhonePromptSuccess />}
            />
            <Route
              exact
              path="/recoverAccount"
              render={(routeProps) => <RecoverAccount {...routeProps} />}
            />
            <Route
              exact
              path="/signup"
              render={(routeProps) => (
                <Register
                  {...routeProps}
                  redirectUri={redirectUri}
                  firstName={siteParams.firstname}
                  lastName={siteParams.lastname}
                  email={siteParams.username}
                  handleUserdetails={this.handleUserdetails}
                  PasswordComplexityFeature={config.featurePasswordComplexity}
                />
              )}
            />
            <Route exact path="/account/signup" render={renderSignUp} />
            <Route exact path="/account/confirm" render={renderSignUp} />
            <Route exact path="/account/claimAccount" render={renderSignUp} />
            <Route
              exact
              path="/account/link/claimAccount"
              render={renderSignUp}
            />
            <Route exact path="/account/registration" render={renderSignUp} />
            <Route exact path="/account/success" render={renderSignUp} />
            <Route exact path="/account/link/confirm" render={renderSignUp} />
            <Route
              exact
              path="/account/link/registration"
              render={renderSignUp}
            />
            <Route exact path="/account/link/success" render={renderSignUp} />
            <Route
              exact
              path="/account/link/mobileprompt"
              render={renderSignUp}
            />
            <Route
              exact
              path="/account/selectprimarylogin"
              render={renderSignUp}
            />
            <Route
              exact
              path="/forgotPassword"
              render={(routeProps) => (
                <ResetPassword
                  {...routeProps}
                  PasswordComplexityFeature={config.featurePasswordComplexity}
                />
              )}
            />

            <Route exact path="/confirm" component={Verify} />
            <Route exact path="/confirmLogin" component={Verify} />
            <Route
              exact
              path="/logout"
              render={(routeProps) => (
                <SignOut
                  {...routeProps}
                  redirectUri={redirectUri}
                  redirectState={siteParams.redirectState}
                  responseType={siteParams.responseType}
                  clientId={siteParams.clientId}
                />
              )}
            />
            <Route exact path="/authorize" component={Authorize} />
            <ProtectedRoute
              exact
              path="/"
              component={Dashboard}
              redirectUri={config.myhstreamUrl}
            />
            <Redirect
              path="/forgotAccount"
              to={{
                pathname: '/forgotPassword',
              }}
            />
            {/* Added Old routes but will continue with the work flow. */}
            <Redirect
              path="/account/forgot-account"
              to={{
                pathname: '/forgotPassword',
              }}
            />
            <Route path="/av" component={AutoVerify} />
            <Route
              exact
              path="/account/sign-in"
              render={(routeProps) => (
                <SignIn
                  {...routeProps}
                  ssoIntegrationOptions={siteParams}
                  handleRememberMeChange={this.handleRememberMeChange}
                  username={siteParams.username}
                />
              )}
            />
            <Route
              exact
              path="/account/need-help"
              render={(routeProps) => <NeedHelp />}
            />
            <Route
              exact
              path="/account/recover-account"
              render={(routeProps) => <RecoverAccount {...routeProps} />}
            />
            <Route
              exact
              path="/account/register"
              render={(routeProps) => (
                <Register
                  {...routeProps}
                  redirectUri={redirectUri}
                  firstName={siteParams.firstname}
                  lastName={siteParams.lastname}
                  email={siteParams.username}
                  handleUserdetails={this.handleUserdetails}
                  PasswordComplexityFeature={config.featurePasswordComplexity}
                />
              )}
            />
            <Route
              exact
              path="/account/forgot-password"
              render={(routeProps) => (
                <ResetPassword
                  {...routeProps}
                  PasswordComplexityFeature={config.featurePasswordComplexity}
                />
              )}
            />
            <Route exact path="/account/verify" component={Verify} />
            <Route
              exact
              path="/account/sign-out"
              render={(routeProps) => (
                <SignOut
                  {...routeProps}
                  redirectUri={redirectUri}
                  redirectState={siteParams.redirectState}
                  responseType={siteParams.responseType}
                  clientId={siteParams.clientId}
                />
              )}
            />
            <Route exact path="/account/authorize" component={Authorize} />
            <Redirect to={{ pathname: '/' }} />
          </AnimatedSwitch>
          {registeredUser && <InactivityWarning />}
        </ScrollToTop>
      </HpaStoreContext.Provider>
    );
  }
}

App.propTypes = {
  isLoadingCurrentUser: PropTypes.bool.isRequired,
  user: userShape,
  // eslint-disable-next-line react/forbid-prop-types
  router: PropTypes.object.isRequired,
  signOutConnect: PropTypes.func.isRequired,
  userArrivedSignedIn: PropTypes.bool.isRequired,
  checkCurrentUserConnect: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  config: PropTypes.object.isRequired,
  promptForPhone: PropTypes.bool,
};

App.defaultProps = {
  user: undefined,
  promptForPhone: undefined,
};

const mapStateToProps = (state) => ({
  user: selectors.getUser(state),
  isLoadingCurrentUser: selectors.isLoadingCurrentUser(state),
  router: state.router,
  userArrivedSignedIn: state.auth.userArrivedSignedIn,
  promptForPhone: selectors.getPromptForPhone(state),
  // NOTE not consuming router will cause this to not re-render when  route
  // change happens which can lead to broken ProtectedRoutes
});

const mapDispatchToProps = {
  checkCurrentUserConnect: checkCurrentUser,
  signOutConnect: signOut,
  bypassImplicitUserCheckConnect: bypassImplicitUserCheck,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);

export { App as PureApp };
